import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, Grid, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import { BiGroup } from 'react-icons/bi';
import { BsPersonLinesFill } from 'react-icons/bs';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { amplitude } from 'wisestep-api';
import {
  WsButton,
  WsColors,
  WsDatePicker,
  WsDateTimePicker,
  WsFilterDialog,
  WsInputNumber,
} from 'wisestep-base';
import { WsSearchFilter } from 'wisestep-website/src/components';

import { JobAdvanceFilterFormProps } from '../../../../../components/filters';
import AccountManagerUserFilter from '../../../../../components/filters/components/AccountManagerFilter';
import WsJobsAdvanceFilter from '../../../../../components/filters/jobsFilter/WsJobsAdvanceFIlter';
import { useWorkspaceContext } from '../../../../../context';
import ErrorLogger from '../../../../../providers/ErrorLogger';

import { ListingSearchProps } from './ListingSearchProps';

interface ListingSearchFilterProps {
  onSearch: (listingSearchFilter: ListingSearchProps) => void;
  onApplyFilters: (jobAdvanceFilterFormProps: JobAdvanceFilterFormProps) => void;
  defaultSearchFilters: ListingSearchProps;
  defaultValues: JobAdvanceFilterFormProps;
  onClearFilters: () => void;
  handleDownloadAction: () => void;
}

const ListingSearchFilter = ({
  onSearch,
  onApplyFilters,
  defaultSearchFilters,
  defaultValues,
  onClearFilters,
  handleDownloadAction,
}: ListingSearchFilterProps) => {
  const workspaceContext = useWorkspaceContext();
  const workspace = useWorkspaceContext();
  const [permissionState, setPermissionState] = useState<boolean>(false);
  const methods = useForm<ListingSearchProps>({ defaultValues: defaultSearchFilters });
  const { handleSubmit, watch, reset } = methods;
  const [selectedDate, setSelectedDate] = useState<string>();
  const [selectedRange, setSelectedRange] = useState<string>();
  const [open, setOpen] = useState<boolean>(false);
  const [minDate, setMinDate] = useState<Date>();
  const [isDateTime, setDateTime] = useState<boolean>(false);

  const formattedDate = (date: string): string => dayjs(date).format('DD/MM/YYYY');

  const fromDate = watch('fromDate');
  const toDate = watch('toDate');
  const minSubmissions = watch('minSubmissions');
  const maxSubmissions = watch('maxSubmissions');
  const watchAccountManagers = watch('accountMangers');
  const onApplyFilter = (data: JobAdvanceFilterFormProps) => onApplyFilters(data);
  useEffect(() => {
    fromDate &&
      toDate &&
      setSelectedDate(`${formattedDate(`${fromDate}`)} to ${formattedDate(`${toDate}`)}`);
    fromDate && setMinDate(fromDate);
    minSubmissions && maxSubmissions && setSelectedRange(`${minSubmissions} to ${maxSubmissions}`);
  }, [fromDate, toDate, minSubmissions, maxSubmissions]);
  useEffect(() => {
    const permissionData = async () => {
      if (workspaceContext?.owns || workspaceContext?.roles?.indexOf('ROLE_DOWNLOAD') !== -1) {
        setPermissionState(true);
      }
    };

    permissionData();
  }, [workspaceContext?.owns, workspaceContext?.roles]);

  const filterAmplitudeEvents = (name: string, clickedOnName: string) =>
    amplitude(name, {
      clickedOn: clickedOnName,
      workspaceName: workspace.name,
      pageName: 'JobListing',
      planType: workspace.planType,
      interfaceType: 'web',
      pageUrl: window.location.href,
    });
  return (
    <>
      <FormProvider {...methods}>
        <Box mb={2} component="form" onSubmit={handleSubmit(onSearch)}>
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            <WsSearchFilter
              className="smart-filters"
              name="searchFilter"
              showDefaultRightActions={true}
              showLeftActions={true}
              showChildren={false}
              onClickFilter={() => {
                filterAmplitudeEvents('Job_Listing_Advance_filters', 'Advanced Filters');
                setOpen(true);
              }}
              clearAllFields={() => {
                filterAmplitudeEvents('Job_Listing_Clear', 'Clear');
                onClearFilters();
                setSelectedDate('Till Current');
                setSelectedRange('All');
                reset({});
              }}
              leftActionsChildren={
                <Grid container item xs={12} pt={2}>
                  <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                    <Grid item xs={4} py={2}>
                      <WsFilterDialog
                        label="Account Manager"
                        name={
                          watchAccountManagers && watchAccountManagers?.length > 0
                            ? `selected ${watchAccountManagers?.length}`
                            : 'All'
                        }
                        icon={<BiGroup size={26} color={WsColors.TheBlues500} />}
                        onClick={() =>
                          filterAmplitudeEvents(
                            'Job_Listing_Smartfilters_Account_Manager',
                            'Account Manager'
                          )
                        }
                      >
                        <Grid px={1} py={2} item xs={4} minWidth={480}>
                          <AccountManagerUserFilter
                            name="accountMangers"
                            multiple={true}
                            isBorderStyle={true}
                          />
                        </Grid>
                      </WsFilterDialog>
                    </Grid>
                  </ErrorBoundary>
                  <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                    <Grid item xs={4} pt={2}>
                      <WsFilterDialog
                        name={selectedDate ?? 'Till Current'}
                        icon={<FaRegCalendarAlt size={21} color={WsColors.TheBlues500} />}
                        label="Posted Date"
                        onClick={() =>
                          filterAmplitudeEvents(
                            'Job_Listing_Smartfilters_posted_date_done',
                            'Posted Date'
                          )
                        }
                      >
                        <Box p={1}>
                          {!isDateTime && (
                            <Stack spacing={2} direction="row" pt={3} pl={1}>
                              <WsDatePicker
                                name="fromDate"
                                label="From Date"
                                disableFuture={true}
                              />
                              <WsDatePicker
                                name="toDate"
                                label="To Date"
                                disableFuture={true}
                                minDate={minDate}
                              />
                            </Stack>
                          )}
                          {isDateTime && (
                            <Stack spacing={2} direction="row" pt={3} pl={1}>
                              <WsDateTimePicker
                                name="fromDate"
                                label="From Date"
                                disableFuture={true}
                              />
                              <WsDateTimePicker
                                name="toDate"
                                label="To Date"
                                disableFuture={true}
                                minDate={minDate}
                              />
                            </Stack>
                          )}
                          {!isDateTime && (
                            <Box px={1}>
                              <WsButton
                                variant="text"
                                size="small"
                                onClick={() => setDateTime(true)}
                              >
                                select Date And Time
                              </WsButton>
                            </Box>
                          )}
                          {isDateTime && (
                            <Box px={1}>
                              <WsButton
                                variant="text"
                                size="small"
                                onClick={() => setDateTime(false)}
                              >
                                select Date Only
                              </WsButton>
                            </Box>
                          )}
                        </Box>
                      </WsFilterDialog>
                    </Grid>
                  </ErrorBoundary>
                  <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                    <Grid item xs={4} py={2} pl={3}>
                      <WsFilterDialog
                        name={selectedRange ?? 'All'}
                        icon={<BsPersonLinesFill size={23} color={WsColors.TheBlues500} />}
                        label="Submission Count"
                        onClick={() =>
                          filterAmplitudeEvents(
                            'Job_Listing_Smartfilters_submission_Count_Done',
                            'Submission Count'
                          )
                        }
                      >
                        <Stack spacing={2} direction="row" p={2}>
                          <WsInputNumber name="minSubmissions" label="Min Submission Count" />
                          <WsInputNumber name="maxSubmissions" label="Max Submission Count" />
                        </Stack>
                      </WsFilterDialog>
                    </Grid>
                  </ErrorBoundary>
                  <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                    {permissionState && (
                      <WsButton
                        onClick={handleDownloadAction}
                        startIcon={<FileDownloadOutlinedIcon />}
                        variant="text"
                      >
                        Download
                      </WsButton>
                    )}
                  </ErrorBoundary>
                </Grid>
              }
            />
          </ErrorBoundary>
        </Box>
      </FormProvider>
      {open && (
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <WsJobsAdvanceFilter
            open={open}
            onApplyFilters={onApplyFilter}
            filterClose={() => setOpen(false)}
            defaultValues={defaultValues}
          />
        </ErrorBoundary>
      )}
    </>
  );
};
export default ListingSearchFilter;
